import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { ModuleShell } from '@/components/layout'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/data-state'
import { RecipeEditor, useItemRecipe, useRecipe, useStockItem } from '@/features/inventory'
import { toApiError } from '@/lib/api/api-error'
import { inventoryRecipePath, paths } from '@/routes/paths'

/** What the page is told on arrival from creating the recipe it shows. */
interface RecipePageState {
  created?: boolean
}

/**
 * One recipe's editor — `/inventory/recipes/:recipeId` — or a new recipe's, at
 * `/inventory/recipes/new?item=<id>`.
 *
 * The recipe is read by id and its item by the id the recipe carries, since the
 * item names the page and bounds what can go into it. A new recipe starts from
 * the item instead, and first checks the item has none: an item has one recipe
 * at most, so if it already has one, that is the one opened.
 */
export function RecipePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { recipeId = null } = useParams<{ recipeId: string }>()
  const [searchParams] = useSearchParams()

  const isNew = !recipeId
  const newItemId = isNew ? searchParams.get('item') : null

  const recipeQuery = useRecipe(recipeId)
  const existing = useItemRecipe(newItemId)

  const itemId = recipeQuery.recipe?.stockItemId ?? newItemId
  const itemQuery = useStockItem(itemId)
  const item = itemQuery.item

  /* Back the way the merchant came when they came from inside the app — a
     filtered list keeps its filter — and to the list itself from a link or a
     reload, where there is nothing behind to go back to. */
  function leave() {
    if (location.key !== 'default') navigate(-1)
    else navigate(paths.inventoryRecipes)
  }

  const created = (location.state as RecipePageState | null)?.created === true

  let body
  if (isNew && !newItemId) {
    body = (
      <EmptyState
        title="Choose a product first."
        description="Open a product's recipe from the Recipes tab."
        action={{ label: 'Go to recipes', onPress: () => navigate(paths.inventoryRecipes) }}
      />
    )
  } else if (isNew && existing.recipe) {
    // The item has its recipe already — usually the draft it was created with.
    body = <Navigate to={inventoryRecipePath(existing.recipe.id)} replace />
  } else if (recipeQuery.error && !recipeQuery.recipe) {
    body =
      toApiError(recipeQuery.error).status === 404 ? (
        <EmptyState
          title="This recipe no longer exists."
          description="It may have been removed. The list has every recipe there is."
          action={{ label: 'Go to recipes', onPress: () => navigate(paths.inventoryRecipes) }}
        />
      ) : (
        <ErrorState subject="this recipe" error={recipeQuery.error} onRetry={recipeQuery.retry} />
      )
  } else if (existing.error) {
    body = (
      <ErrorState subject="this product's recipe" error={existing.error} onRetry={existing.retry} />
    )
  } else if (itemQuery.error && !item) {
    body = (
      <ErrorState
        subject="the product this recipe makes"
        error={itemQuery.error}
        onRetry={itemQuery.retry}
      />
    )
  } else if (recipeQuery.isLoading || existing.isLoading || !item) {
    body = <LoadingState />
  } else {
    body = (
      <RecipeEditor
        // A different recipe — or the new one turning into a saved one — starts
        // the editor over rather than carrying the last one's form along.
        key={recipeQuery.recipe?.id ?? `new:${item.id}`}
        item={item}
        recipe={recipeQuery.recipe}
        justCreated={created}
        onCreated={(recipe) =>
          navigate(inventoryRecipePath(recipe.id), {
            replace: true,
            state: { created: true } satisfies RecipePageState,
          })
        }
        onLeave={leave}
      />
    )
  }

  return <ModuleShell>{body}</ModuleShell>
}
